import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    FaSpinner, FaSearch, FaHome, FaSyncAlt, FaCalendarCheck,
    FaDollarSign, FaFilter, FaMoneyBillWave, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Importe os estilos 'ui' e o modal de baixa de parcela (que criaremos a seguir)
import { ui } from '../utils/styles';
import BaixaParcelaModal from './BaixaParcelaModal';

const RecebimentoControlScreen = () => {
    const navigate = useNavigate();

    const [allParcelas, setAllParcelas] = useState([]);
    const [clientesContratantes, setClientesContratantes] = useState([]);
    const [devedores, setDevedores] = useState([]);

    // Filtros
    const [filterClienteId, setFilterClienteId] = useState('');
    const [filterDevedorId, setFilterDevedorId] = useState('');
    const [filterStatus, setFilterStatus] = useState('Pendente'); // Padrão: Pendente
    const [filterDataInicio, setFilterDataInicio] = useState('');
    const [filterDataFim, setFilterDataFim] = useState('');

    const [loading, setLoading] = useState(false);

    // Estado do Modal de Baixa
    const [showBaixaModal, setShowBaixaModal] = useState(false);
    const [selectedParcela, setSelectedParcela] = useState(null);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    // Opções de Status
    const statusOptions = ['Todos', 'Pendente', 'Pago', 'Atrasado', 'Cancelado'];

    // --- Efeitos de Inicialização e Carregamento ---

    useEffect(() => {
        fetchClientesAndDevedores();
    }, []);

    useEffect(() => {
        fetchParcelas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClienteId]); // Dispara ao mudar o cliente principal

    const fetchClientesAndDevedores = async () => {
        try {
            const clientesRes = await axios.get(`${URL_BACKEND}/cliente`, { headers: authHeader });
            setClientesContratantes(clientesRes.data || []);

            const devedoresRes = await axios.get(`${URL_BACKEND}/devedor`, { headers: authHeader });
            setDevedores(devedoresRes.data || []);

            const storedEmitenteId = localStorage.getItem('userIdEmitente');
            if (storedEmitenteId) {
                setFilterClienteId(String(storedEmitenteId));
            }

        } catch (error) {
            toast.error('Erro ao carregar dados de Cliente/Devedor.');
        }
    };

    const fetchParcelas = async () => {
        setLoading(true);

        // Constrói a query de filtro
        const params = new URLSearchParams();
        if (filterClienteId) params.append('idCliente', filterClienteId);
        if (filterDevedorId) params.append('idDevedor', filterDevedorId);
        if (filterStatus && filterStatus !== 'Todos') params.append('status', filterStatus);
        if (filterDataInicio) params.append('dataVencimentoDe', filterDataInicio);
        if (filterDataFim) params.append('dataVencimentoAte', filterDataFim);

        try {
            // Endpoint de busca de todas as parcelas
            const { data } = await axios.get(
                `${URL_BACKEND}/parcela`,
                { headers: authHeader, params }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllParcelas(arr);
            toast.success(`Parcelas carregadas: ${arr.length} registros.`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar parcelas.');
            setAllParcelas([]);
        } finally {
            setLoading(false);
        }
    };

    // --- Lógica de Baixa (Modal) ---
    const handleBaixa = (parcela) => {
        if (parcela.status === 'Pago') {
            toast('Esta parcela já foi paga.', { icon: '💰' });
            return;
        }
        setSelectedParcela(parcela);
        setShowBaixaModal(true);
    };

    // --- Funções Auxiliares ---

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'R$ 0,00';
        return `R$ ${Number(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    };

    // Filtra Devedores com base no Cliente selecionado (para o filtro)
    const filteredDevedores = useMemo(() => {
        if (!filterClienteId) return devedores || [];
        return devedores.filter(d => String(d.id_cliente_contratante) === String(filterClienteId));
    }, [devedores, filterClienteId]);

    // --- Cálculos de Totalização ---
    const totais = useMemo(() => {
        return allParcelas.reduce((acc, p) => {
            const valor = Number(p.valor);
            if (p.status === 'Pago') {
                acc.totalPago += valor;
            } else if (p.status === 'Pendente' || p.status === 'Atrasado') {
                acc.totalPendente += valor;
            }
            return acc;
        }, { totalPendente: 0, totalPago: 0 });
    }, [allParcelas]);


    // --- Renderização ---

    return (
        <div style={ui.container}>

            {/* Cabeçalho */}
            <div style={ui.header}>
                <h1 style={ui.title}>
                    <FaMoneyBillWave style={{ marginRight: 8 }} /> Controle de Recebimentos (Parcelas)
                </h1>
                <button
                    onClick={() => navigate('/home')}
                    style={ui.homeButton}
                >
                    <FaHome /> Início
                </button>
            </div>

            {/* Cards de Totalização */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                <div style={{ ...ui.card, padding: 16, background: '#fef3c7', border: '1px solid #d97706' }}>
                    <h3 style={{ margin: 0, color: '#d97706' }}>A Receber (Filtros Atuais)</h3>
                    <p style={{ ...ui.tdMono, fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706' }}>
                        {formatCurrency(totais.totalPendente)}
                    </p>
                </div>
                <div style={{ ...ui.card, padding: 16, background: '#d1fae5', border: '1px solid #059669' }}>
                    <h3 style={{ margin: 0, color: '#059669' }}>Total Recebido (Filtros Atuais)</h3>
                    <p style={{ ...ui.tdMono, fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                        {formatCurrency(totais.totalPago)}
                    </p>
                </div>
                <div style={{ ...ui.card, padding: 16, background: '#eff6ff', border: '1px solid #3b82f6' }}>
                    <h3 style={{ margin: 0, color: '#3b82f6' }}>Total de Parcelas</h3>
                    <p style={{ ...ui.tdMono, fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                        {formatCurrency(totais.totalPendente + totais.totalPago)}
                    </p>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ ...ui.filters, flexWrap: 'wrap' }}>
                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterCliente">Cliente</label>
                    <select
                        id="filterCliente"
                        style={ui.select}
                        value={filterClienteId}
                        onChange={e => {
                            setFilterClienteId(e.target.value);
                            setFilterDevedorId(''); // Reseta devedor ao mudar o cliente
                        }}
                        disabled={loading}
                    >
                        <option value="">Todos os Clientes</option>
                        {clientesContratantes.map(c => (
                            <option key={c.id} value={String(c.id)}>{c.nome}</option>
                        ))}
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterDevedor">Devedor</label>
                    <select
                        id="filterDevedor"
                        style={ui.select}
                        value={filterDevedorId}
                        onChange={e => setFilterDevedorId(e.target.value)}
                        disabled={loading || !filterClienteId}
                    >
                        <option value="">Todos os Devedores</option>
                        {filteredDevedores.map(d => (
                            <option key={d.id} value={String(d.id)}>{d.nome}</option>
                        ))}
                        {filterClienteId && filteredDevedores.length === 0 && (
                            <option disabled>Nenhum devedor para este cliente.</option>
                        )}
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterStatus">Status</label>
                    <select
                        id="filterStatus"
                        style={ui.select}
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        disabled={loading}
                    >
                        {statusOptions.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterDataInicio">Vencimento De</label>
                    <input
                        id="filterDataInicio"
                        type="date"
                        style={ui.input}
                        value={filterDataInicio}
                        onChange={e => setFilterDataInicio(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterDataFim">Vencimento Até</label>
                    <input
                        id="filterDataFim"
                        type="date"
                        style={ui.input}
                        value={filterDataFim}
                        onChange={e => setFilterDataFim(e.target.value)}
                        disabled={loading}
                    />
                </div>

                {/* Botões de Ação */}
                <div style={{ ...ui.actionsRow, alignSelf: 'flex-end', minWidth: '150px' }}>
                    <button style={ui.buttonPrimary} onClick={fetchParcelas} disabled={loading}>
                        {loading ? <FaSpinner className="fa-spin" /> : <FaSearch />}
                    </button>
                    <button style={{ ...ui.buttonPrimary, background: '#d97706' }} onClick={() => {
                        setFilterClienteId(localStorage.getItem('userIdEmitente') || '');
                        setFilterDevedorId('');
                        setFilterStatus('Pendente');
                        setFilterDataInicio('');
                        setFilterDataFim('');
                    }} disabled={loading} title="Limpar Filtros (Exceto Cliente)">
                        <FaTimes />
                    </button>
                </div>
            </div>

            {/* TABELA DE PARCELAS */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p style={{ padding: 16 }}>Carregando Parcelas...</p>
                ) : allParcelas.length === 0 ? (
                    <p style={{ color: 'red', padding: 16 }}>Nenhuma parcela encontrada com os filtros atuais.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    <th style={{ ...ui.th, width: '60px' }}>#</th>
                                    <th style={{ ...ui.th, width: '100px' }}>Nº Parcela</th>
                                    <th style={{ ...ui.th, width: '200px' }}>Devedor</th>
                                    <th style={{ ...ui.th, width: '120px' }}>Vencimento</th>
                                    <th style={{ ...ui.th, width: '120px' }}>Valor</th>
                                    <th style={{ ...ui.th, width: '100px' }}>Status</th>
                                    <th style={{ ...ui.th, width: '120px' }}>Data Pagto</th>
                                    <th style={{ ...ui.th, width: '100px' }}>Processo</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allParcelas.map((p, idx) => {
                                    const statusColor = p.status === 'Pago' ? '#059669' :
                                        p.status === 'Atrasado' ? '#ef4444' : '#3b82f6';

                                    return (
                                        <tr key={p.id} style={{ ...ui.tr, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                            <td style={ui.tdMono}>#{p.id}</td>
                                            <td style={ui.tdMono}>{p.numero} / {p.total_parcelas}</td> {/* Assumindo que o total de parcelas está disponível */}
                                            <td style={ui.tdClamp}>{p.nome_devedor}</td>
                                            <td style={ui.tdMono}>
                                                {p.data_vencimento ? new Date(p.data_vencimento).toLocaleDateString('pt-BR') : 'N/A'}
                                            </td>
                                            <td style={ui.tdMono}>{formatCurrency(p.valor)}</td>
                                            <td style={ui.tdClamp}>
                                                <span style={{ fontWeight: 'bold', color: statusColor }}>{p.status}</span>
                                            </td>
                                            <td style={ui.tdMono}>
                                                {p.data_pagamento ? new Date(p.data_pagamento).toLocaleDateString('pt-BR') : 'N/A'}
                                            </td>
                                            <td style={ui.tdMono}>
                                                Proc #{p.id_processo_acordo}
                                            </td>
                                            <td style={ui.tdActions}>
                                                {p.status !== 'Pago' && (
                                                    <button
                                                        title="Dar Baixa (Pagar)"
                                                        style={{ ...ui.actionEdit, background: '#059669' }}
                                                        onClick={() => handleBaixa(p)}
                                                    >
                                                        <FaCalendarCheck />
                                                    </button>
                                                )}
                                                {/* Outras ações (Ex: estornar, editar) */}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Baixa */}
            <BaixaParcelaModal
                show={showBaixaModal}
                onClose={() => setShowBaixaModal(false)}
                parcela={selectedParcela}
                onBaixa={fetchParcelas} // Recarrega a lista após a baixa
            />

        </div>
    );
};

export default RecebimentoControlScreen;